import {
  useState,
  useRef,
  useCallback,
  useMemo,
  useId,
  useTransition,
  useEffect,
} from "react";
import { useEffectEvent } from "./use-effect-event";
import { toast } from "sonner";
import { Validator } from "convex/values";
import { validate } from "convex-helpers/validators";
import { getErrorMessage } from "@/lib/utils";
import { parsePhone, type PhoneState } from "@/hooks/parsers/phoneParser";

type ConvexValidator = Record<string, Validator<any, any, any>>;

type FormFieldMapper<TValidator extends ConvexValidator> = {
  [K in keyof TValidator]?: string;
};

// Parser para campos custom (ej: teléfono, fechas, etc)
export interface FieldParser<TForm = any, TDb = any> {
  parse: (dbValue: TDb) => TForm; // DB → Form
  format: (formValue: TForm) => TDb; // Form → DB
  validate?: (formValue: TForm) => boolean;
  getValidationMessage?: (formValue: TForm) => string | null;
}

type FormMode = "defaultValue" | "controlled" | "hybrid";

type UseConvexFormConfig<TValidator extends ConvexValidator, TData = any> = {
  isUpdate: boolean;
  validator: TValidator;
  fieldMapper?: FormFieldMapper<TValidator>;
  buildData?: (formData: FormData, validatedFields: Partial<TData>) => TData;
  onSubmit: (data: TData, isUpdate: boolean) => void | Promise<void>;
  checkUser?: () => boolean;
  userErrorMessage?: string;
  customValidations?: Array<{
    field: string;
    message: string;
    validate: (value: any) => boolean;
  }>;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  defaultValues?: Partial<TData>;
  minLoadingTime?: number; // Tiempo mínimo de carga en ms (default: 500ms)
  silentErrors?: boolean; // Desactivar toasts de error (útil cuando se usa con useToastMutation)

  // Nuevas opciones para modo híbrido
  mode?: FormMode; // Modo de operación del formulario
  controlledFields?: (keyof TData)[]; // Campos que serán controlados
  parsers?: Partial<Record<keyof TData, FieldParser<any, any>>>; // Parsers para campos custom
  onFieldChange?: (field: keyof TData, value: any) => void; // Callback para cambios de campos
  initialData?: Partial<TData>; // Datos iniciales (auto-reset cuando cambian)
  initialDataId?: string | number; // ID estable para detectar cambios (ej: record._id)
};

// Registry global para labels de campos
const fieldLabelsRegistry = new Map<string, string>();

function getFieldLabel(fieldName: string, formId: string): string {
  // Intentar obtener el label del registry
  const registryKey = `${formId}-${fieldName}`;
  const registeredLabel = fieldLabelsRegistry.get(registryKey);

  if (registeredLabel) {
    return registeredLabel;
  }

  // Fallback: generar label desde el nombre del campo
  return fieldName
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

export function registerFieldLabel(
  fieldName: string,
  label: string,
  formId: string,
) {
  const registryKey = `${formId}-${fieldName}`;
  fieldLabelsRegistry.set(registryKey, label);
}

export function clearFieldLabels(formId: string) {
  // Limpiar solo los labels de un formulario específico
  Array.from(fieldLabelsRegistry.keys())
    .filter((key) => key.startsWith(`${formId}-`))
    .forEach((key) => fieldLabelsRegistry.delete(key));
}

function isFieldOptional(fieldValidator: Validator<any, any, any>): boolean {
  // Un campo es opcional si undefined pasa la validación
  try {
    return validate(fieldValidator, undefined, { throw: false });
  } catch {
    return false;
  }
}

export function useConvexForm<TValidator extends ConvexValidator, TData = any>(
  config: UseConvexFormConfig<TValidator, TData>,
) {
  // Generar un formId único automáticamente usando useId
  const uniqueId = useId();
  const formId = useMemo(() => `form-${uniqueId}`, [uniqueId]);

  const [isPending, startTransition] = useTransition();
  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState<Partial<TData>>(() => {
    // Si hay initialData Y no es un objeto vacío, parsearlo y usarlo directamente
    if (
      config.initialData !== undefined &&
      config.initialData !== null &&
      Object.keys(config.initialData).length > 0
    ) {
      const parsersObj = config.parsers || {};
      if (Object.keys(parsersObj).length === 0) {
        return config.initialData;
      }

      // Aplicar parsers sincrónicamente
      const parsed = { ...config.initialData };
      for (const [key, parser] of Object.entries(parsersObj) as [
        keyof TData,
        FieldParser,
      ][]) {
        if (parser && parser.parse && config.initialData[key] !== undefined) {
          parsed[key] = parser.parse(config.initialData[key]);
        }
      }
      return parsed;
    }

    // Fallback a defaultValues
    return config.defaultValues || ({} as Partial<TData>);
  });

  const formRef = useRef<HTMLFormElement | null>(null);

  // Memoizar valores que no cambian
  const {
    isUpdate,
    validator,
    fieldMapper,
    customValidations,
    mode = "defaultValue",
    controlledFields = [],
    initialData,
    initialDataId,
  } = config;

  const parsers = useMemo(
    () =>
      config.parsers ||
      ({} as Partial<Record<keyof TData, FieldParser<any, any>>>),
    [config.parsers],
  );

  // Usar useEffectEvent para callbacks que siempre deben ver valores frescos
  const onSubmitEvent = useEffectEvent(config.onSubmit);
  const onSuccessEvent = useEffectEvent(() => config.onSuccess?.());
  const onErrorEvent = useEffectEvent((error: string) =>
    config.onError?.(error),
  );
  const checkUserEvent = useEffectEvent(() => config.checkUser?.() ?? true);
  const buildDataEvent = useEffectEvent(
    config.buildData ||
      ((_: FormData, validatedFields: Partial<TData>): TData =>
        validatedFields as TData),
  );

  // Helper para enfocar y hacer scroll a un campo con error
  const focusErrorField = useCallback((fieldName: string) => {
    const fieldElement = formRef.current?.elements.namedItem(
      fieldName,
    ) as HTMLInputElement | null;
    if (fieldElement) {
      fieldElement.focus();
      fieldElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, []);

  const validateFormFromConvex = useCallback(
    (
      formData: FormData,
      silent = false,
    ): { isValid: boolean; errorField?: string } => {
      for (const [fieldName, fieldValidator] of Object.entries(validator)) {
        // Saltar validación de 'id' en modo create
        if (fieldName === "id" && !isUpdate) continue;

        const formFieldName = fieldMapper?.[fieldName] || fieldName;

        // Para campos controlados, obtener del estado
        let value: any;
        const typedFieldName = fieldName as keyof TData;

        if (controlledFields.includes(typedFieldName)) {
          value = fields[typedFieldName];
        } else {
          value = formData.get(formFieldName);
        }

        const fieldLabel = getFieldLabel(formFieldName as string, formId);

        // Verificar si el campo es opcional
        const optional = isFieldOptional(fieldValidator);

        // Verificar si está vacío
        const isEmpty =
          value === undefined ||
          value === null ||
          (typeof value === "string" && value.trim() === "");

        // Validar campos requeridos vacíos
        if (!optional && isEmpty) {
          if (!silent) {
            toast.error(`${fieldLabel} es requerido`);
            focusErrorField(formFieldName as string);
          }
          return { isValid: false, errorField: formFieldName as string };
        }

        // Si tiene valor, validar solo el tipo básico con Convex
        // NO validar con parsers aquí (eso se hace en buildData)
        if (!isEmpty) {
          // Para campos con parsers, saltar la validación de Convex
          // porque el parser se encargará de formatear correctamente
          const hasParser = parsers && typedFieldName in parsers;

          if (!hasParser) {
            try {
              const isValid = validate(fieldValidator, value, {
                throw: false,
              });
              if (!isValid) {
                if (!silent) {
                  toast.error(`${fieldLabel} tiene un formato inválido`);
                  focusErrorField(formFieldName as string);
                }
                return {
                  isValid: false,
                  errorField: formFieldName as string,
                };
              }
            } catch (error) {
              if (!silent) {
                toast.error(`${fieldLabel} tiene un formato inválido`);
                focusErrorField(formFieldName as string);
              }
              return {
                isValid: false,
                errorField: formFieldName as string,
              };
            }
          }
        }
      }

      // Validaciones personalizadas adicionales
      if (customValidations) {
        for (const validation of customValidations) {
          const value = formData.get(validation.field);
          if (!validation.validate(value)) {
            if (!silent) {
              toast.error(validation.message);
              focusErrorField(validation.field);
            }
            return { isValid: false, errorField: validation.field };
          }
        }
      }

      return { isValid: true };
    },
    [
      validator,
      fieldMapper,
      customValidations,
      isUpdate,
      formId,
      focusErrorField,
      controlledFields,
      fields,
      parsers,
    ],
  );

  const extractValidatedFields = useCallback(
    (formData: FormData): Partial<TData> => {
      const extractedFields: any = {};

      for (const fieldName of Object.keys(validator)) {
        if (fieldName === "id" && !isUpdate) continue;

        const typedFieldName = fieldName as keyof TData;

        // Si es campo controlado, tomar del estado en lugar de FormData
        if (controlledFields.includes(typedFieldName)) {
          let fieldValue = fields[typedFieldName];

          // Aplicar parser/formatter si existe - acceso seguro
          if (parsers && typedFieldName in parsers) {
            const parser = parsers[typedFieldName];
            if (parser && fieldValue !== undefined) {
              // Validar con el parser si tiene validación
              if (parser.validate && !parser.validate(fieldValue)) {
                const message =
                  parser.getValidationMessage?.(fieldValue) ||
                  `${getFieldLabel(fieldName, formId)} tiene un formato inválido`;
                focusErrorField(fieldName);
                throw new Error(message);
              }

              // Formatear
              if (parser.format) {
                fieldValue = parser.format(fieldValue);
              }
            }
          }

          if (fieldValue !== undefined) {
            extractedFields[fieldName] = fieldValue;
          }
          continue;
        }

        const formFieldName =
          fieldMapper?.[fieldName as keyof TValidator] || fieldName;
        let value = formData.get(formFieldName as string);

        // Permitir strings vacios para campos opcionales (para poder limpiarlos)
        const fieldValidator = validator[fieldName as keyof TValidator];
        const optional = isFieldOptional(fieldValidator);
        const isEmptyString =
          value !== null && value !== undefined && value === "";

        if (
          value !== null &&
          value !== undefined &&
          (!isEmptyString || optional)
        ) {
          // Aplicar parser/formatter si existe - acceso seguro
          if (parsers && typedFieldName in parsers) {
            const parser = parsers[typedFieldName];
            if (parser) {
              // Validar con el parser si tiene validación
              if (parser.validate && !parser.validate(value)) {
                const message =
                  parser.getValidationMessage?.(value) ||
                  `${getFieldLabel(formFieldName as string, formId)} tiene un formato inválido`;
                focusErrorField(formFieldName as string);
                throw new Error(message);
              }

              // Formatear
              if (parser.format) {
                value = parser.format(value);
              }
            }
          }

          extractedFields[fieldName] = value;
        }
      }

      return extractedFields;
    },
    [
      validator,
      fieldMapper,
      isUpdate,
      controlledFields,
      fields,
      parsers,
      formId,
      focusErrorField,
    ],
  );

  // Parse: aplicar parsers a datos de DB para formato del formulario
  const parseData = useCallback(
    (data: Partial<TData>): Partial<TData> => {
      if (Object.keys(parsers).length === 0) {
        return data;
      }

      const parsed = { ...data };
      for (const [key, parser] of Object.entries(parsers) as [
        keyof TData,
        FieldParser,
      ][]) {
        if (parser && parser.parse && data[key] !== undefined) {
          parsed[key] = parser.parse(data[key]);
        }
      }
      return parsed;
    },
    [parsers],
  );

  // Format: aplicar parsers inversos para formato de DB
  const formatData = useCallback(
    (data: Partial<TData>): Partial<TData> => {
      if (Object.keys(parsers).length === 0) {
        return data;
      }

      const formatted = { ...data };
      for (const [key, parser] of Object.entries(parsers) as [
        keyof TData,
        FieldParser,
      ][]) {
        if (parser && parser.format && data[key] !== undefined) {
          formatted[key] = parser.format(data[key]);
        }
      }
      return formatted;
    },
    [parsers],
  );

  // Watch: función para obtener el valor actual de un campo
  const watch = useCallback(
    (fieldName?: keyof TData): any => {
      if (!fieldName) {
        return fields;
      }
      return fields[fieldName];
    },
    [fields],
  );

  // SetValue: función para actualizar un campo manualmente
  const setValue = useCallback(
    (fieldName: keyof TData, value: any) => {
      setFields((prev) => ({
        ...prev,
        [fieldName]: value,
      }));

      // Callback opcional para cambios de campo
      config.onFieldChange?.(fieldName, value);

      // También actualizar el campo en el DOM si existe (para campos no controlados)
      if (formRef.current && !controlledFields.includes(fieldName)) {
        const input = formRef.current.elements.namedItem(
          fieldName as string,
        ) as HTMLInputElement | null;
        if (input) {
          input.value = value?.toString() || "";
        }
      }
    },
    [controlledFields, config],
  );

  // GetValues: obtener todos los valores actuales del formulario
  const getValues = useCallback((): Partial<TData> => {
    return fields;
  }, [fields]);

  // Reset: resetear el formulario (con parsers)
  const reset = useCallback(
    (values?: Partial<TData>) => {
      const rawValues = values || config.defaultValues || {};
      // Aplicar parsers si existen
      const parsedValues = parseData(rawValues);

      // Filtrar campos controlados que están vacíos para que useField use su defaultValue
      const filteredFields = Object.fromEntries(
        Object.entries(parsedValues).filter(([key, value]) => {
          // Si es campo controlado y el valor original estaba vacío/undefined,
          // no lo incluimos para que useField use su defaultValue
          if (controlledFields.includes(key as keyof TData)) {
            const originalValue = (rawValues as any)[key];
            return (
              originalValue !== undefined &&
              originalValue !== null &&
              originalValue !== ""
            );
          }
          return true;
        }),
      ) as Partial<TData>;

      startTransition(() => {
        setFields(filteredFields);
      });

      if (formRef.current) {
        formRef.current.reset();
        // Actualizar valores en el DOM (solo campos no controlados)
        Object.entries(parsedValues).forEach(([key, value]) => {
          if (!controlledFields.includes(key as keyof TData)) {
            const input = formRef.current!.elements.namedItem(
              key,
            ) as HTMLInputElement | null;
            if (input) {
              input.value = value?.toString() || "";
            }
          }
        });
      }
    },
    [parseData, controlledFields, config.defaultValues],
  );

  // ✅ SOLUCIÓN: Auto-reset cuando cambia initialDataId O cuando initialDataId es undefined
  // Guardar referencia previa para detectar cambios
  const prevInitialDataIdRef = useRef(initialDataId);

  useEffect(() => {
    // Detectar si cambió el ID o si se pasó de tener ID a no tenerlo (crear nuevo)
    const idChanged = prevInitialDataIdRef.current !== initialDataId;

    if (idChanged) {
      // Actualizar referencia
      prevInitialDataIdRef.current = initialDataId;

      // Si initialData está definido, resetear con esos datos
      if (initialData !== undefined) {
        reset(initialData);
      } else {
        // Si no hay initialData, resetear completamente los campos controlados
        const emptyFields = controlledFields.reduce((acc, field) => {
          acc[field] = undefined;
          return acc;
        }, {} as Partial<TData>);

        startTransition(() => {
          setFields(emptyFields);
        });
      }
    }
  }, [initialDataId, initialData, reset, controlledFields]);

  // Función para sincronizar el estado cuando cambian los inputs
  const handleChange = useCallback(
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => {
      const { name, value } = e.target;

      // Actualizar campos inmediatamente
      setFields((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Callback opcional
      config.onFieldChange?.(name as keyof TData, value);
    },
    [config],
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Guardar referencia del form
    formRef.current = e.currentTarget;

    const formData = new FormData(e.currentTarget);

    // Validar usuario
    if (!checkUserEvent()) {
      toast.error(config.userErrorMessage || "El usuario no está registrado");
      return;
    }

    // Validar formulario
    const validationResult = validateFormFromConvex(formData);
    if (!validationResult.isValid) {
      return;
    }

    // Solo activar loading después de que pase la validación
    setIsLoading(true);

    // Crear promesa de delay mínimo para garantizar feedback visual
    const minLoadingMs = config.minLoadingTime ?? 500;
    const minLoadingTime = new Promise((resolve) =>
      setTimeout(resolve, minLoadingMs),
    );

    try {
      // Extraer y construir datos
      const validatedFields = extractValidatedFields(formData);

      // buildData es responsable de formatear los campos
      const data = buildDataEvent(formData, validatedFields);

      // Ejecutar submit y delay mínimo en paralelo
      await Promise.all([onSubmitEvent(data, isUpdate), minLoadingTime]);

      // Éxito
      onSuccessEvent();
    } catch (error) {
      // No esperar delay mínimo para errores de validación
      // Solo esperar si ya pasó la validación inicial
      const errorMessage = getErrorMessage(error);

      // Solo mostrar toast si no está en modo silencioso
      if (!config.silentErrors) {
        toast.error(errorMessage);
      }

      onErrorEvent(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Helper mejorado para binding automático de campos controlados
  const useField = useCallback(
    (fieldName: keyof TData, defaultValue?: any) => {
      // Si el campo tiene valor en fields, usarlo; sino usar defaultValue
      const value =
        fields[fieldName] !== undefined ? fields[fieldName] : defaultValue;
      const onChange = (newValue: any) => setValue(fieldName, newValue);
      return { value, onChange };
    },
    [fields, setValue],
  );

  const useCheckboxMapping = useCallback(
    <T = any>(config: {
      fieldName: keyof TData;
      trueValue: T;
      falseValue: T;
      defaultValue?: T;
    }) => {
      const { fieldName, trueValue, falseValue, defaultValue } = config;

      // Obtener el valor actual del campo
      const currentValue =
        fields[fieldName] !== undefined ? fields[fieldName] : defaultValue;

      return {
        checked: currentValue === trueValue,
        onCheckedChange: (checked: boolean) => {
          setValue(fieldName, checked ? trueValue : falseValue);
        },
      };
    },
    [fields, setValue],
  );

  const useSelectMapping = useCallback(
    <T = string>(config: { fieldName: keyof TData; defaultValue?: T }) => {
      const { fieldName, defaultValue } = config;

      // Obtener el valor actual del campo
      const currentValue =
        fields[fieldName] !== undefined ? fields[fieldName] : defaultValue;

      return {
        value: currentValue as string,
        onValueChange: (value: string) => {
          setValue(fieldName, value as any);
        },
        defaultValue: defaultValue,
      };
    },
    [fields, setValue],
  );

  const usePhoneMapping = useCallback(
    (config: { fieldName: keyof TData; defaultValue?: string }) => {
      const { fieldName, defaultValue } = config;

      // Obtener el valor actual del campo (ya parseado por el parser)
      const fieldValue = fields[fieldName];
      let currentValue: any;

      if (fieldValue !== undefined) {
        // Si ya tenemos un valor en fields, usarlo
        currentValue = fieldValue;
      } else {
        // Si no hay valor, parsear el defaultValue
        currentValue = parsePhone(defaultValue || "");
      }

      // Asegurar que siempre devolvemos un PhoneState
      // Si por alguna razón currentValue no es PhoneState, parsearlo
      const phoneState: PhoneState =
        typeof currentValue === "object" &&
        "countryCode" in currentValue &&
        "number" in currentValue
          ? (currentValue as PhoneState)
          : parsePhone(typeof currentValue === "string" ? currentValue : "");

      return {
        value: phoneState,
        onChange: (value: PhoneState) => {
          setValue(fieldName, value);
        },
      };
    },
    [fields, setValue],
  );
  return {
    handleSubmit,
    isLoading: isLoading,
    validateForm: validateFormFromConvex,
    // Funciones de control
    watch,
    setValue,
    getValues,
    reset,
    fields,
    handleChange,
    useField,
    useCheckboxMapping,
    useSelectMapping,
    usePhoneMapping,
    // Parsers
    parseData,
    formatData,
    // FormId para pasar al FormDialog
    formId,
    // Estado de transición
    isPending,
    // Modo actual
    mode,
    controlledFields,
  };
}
